// ─────────────────────────────────────────────────────────────────────────
// FinanceListToolbar — single filter toolbar for the Billing / Payroll lists.
// Fully controlled: the host page owns state, debouncing and fetching.
// ─────────────────────────────────────────────────────────────────────────
import { Button, DatePicker, Input, Select, Tooltip } from 'antd';
import {
  ClearOutlined,
  DownloadOutlined,
  ReloadOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import './finance-components.css';

const CYCLE_OPTIONS = ['Weekly', 'Bi-Weekly', 'Monthly'].map((value) => ({ value, label: value }));

export default function FinanceListToolbar({
  search,
  onSearchChange,
  searchPlaceholder = 'Search employee, client, employer, project…',
  monthValue,
  onMonthChange,
  cycleValue,
  onCycleChange,
  statusValue,
  onStatusChange,
  statusOptions = [],
  chips = [],
  activeChips = [],
  onToggleChip,
  onReset,
  onRefresh,
  onExport,
  exporting = false,
  disabled = false,
}) {
  return (
    <div className="fin-toolbar">
      <div className="fin-toolbar__main">
        <div className="fin-toolbar__filters">
          <Input
            className="fin-toolbar__search"
            size="large"
            allowClear
            prefix={<SearchOutlined className="fin-toolbar__search-icon" />}
            placeholder={searchPlaceholder}
            value={search}
            onChange={(event) => onSearchChange?.(event.target.value)}
            disabled={disabled}
          />
          <DatePicker
            className="fin-toolbar__control"
            picker="month"
            allowClear
            placeholder="All months"
            value={monthValue}
            onChange={onMonthChange}
            disabled={disabled}
          />
          <Select
            className="fin-toolbar__control"
            allowClear
            placeholder="Payment cycle"
            value={cycleValue || undefined}
            onChange={(value) => onCycleChange?.(value ?? '')}
            options={CYCLE_OPTIONS}
            disabled={disabled}
            popupMatchSelectWidth={false}
          />
          <Select
            className="fin-toolbar__control"
            allowClear
            placeholder="Status"
            value={statusValue || undefined}
            onChange={(value) => onStatusChange?.(value ?? '')}
            options={statusOptions}
            disabled={disabled}
            popupMatchSelectWidth={false}
          />
        </div>
        <div className="fin-toolbar__actions">
          <Tooltip title="Reset filters">
            <Button icon={<ClearOutlined />} onClick={onReset} disabled={disabled} aria-label="Reset filters" />
          </Tooltip>
          <Tooltip title="Refresh">
            <Button icon={<ReloadOutlined />} onClick={onRefresh} disabled={disabled} aria-label="Refresh" />
          </Tooltip>
          <Button icon={<DownloadOutlined />} onClick={onExport} loading={exporting} disabled={disabled}>
            Export
          </Button>
        </div>
      </div>
      {chips.length ? (
        <div className="fin-toolbar__chips">
          <span className="fin-toolbar__chips-label">
            <ThunderboltOutlined /> Quick filters
          </span>
          {chips.map((chip) => {
            const active = activeChips.includes(chip.key);
            return (
              <button
                type="button"
                key={chip.key}
                className={`fin-chip${active ? ' fin-chip--active' : ''}`}
                onClick={() => onToggleChip?.(chip.key)}
                disabled={disabled}
              >
                {chip.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
